import { useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { money } from "@/components/settlement/SupplierSettlement";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

export async function downloadFile(url, fallbackName) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || fallbackName;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) { toast.error("Download failed"); }
}

function OverrideForm({ payment, open, onDone }) {
  const [amts, setAmts] = useState(() => Object.fromEntries(payment.allocations.map((a) => [a.obligation_id, a.amount_cents / 100])));
  const [reason, setReason] = useState("");
  const save = async () => {
    const allocations = Object.entries(amts).filter(([, v]) => Number(v) > 0).map(([obligation_id, amount]) => ({ obligation_id, amount: String(amount) }));
    try { await api.post(`/admin/supplier-payments/${payment.id}/reallocate`, { allocations, reason }); toast.success("Allocation overridden (audited)"); onDone(); }
    catch (e) { err(e); }
  };
  const mine = Object.fromEntries(payment.allocations.map((a) => [a.obligation_id, a.amount_cents / 100]));
  const rows = [...open, ...payment.allocations.filter((a) => !open.find((o) => o.id === a.obligation_id)).map((a) => ({ id: a.obligation_id, order_number: "(currently allocated)", sku: "", open_usd: "0" }))];
  return (
    <div className="mt-2 space-y-1 rounded-md bg-sand/40 p-3" data-testid={`override-form-${payment.id}`}>
      {rows.map((o) => (
        <div key={o.id} className="flex flex-wrap items-center gap-2">
          <span className="w-56 truncate">{o.order_number} {o.sku} · open {money(Number(o.open_usd) + (mine[o.id] || 0))}</span>
          <Input type="number" min="0" step="0.01" defaultValue={mine[o.id] || ""} onChange={(e) => setAmts((x) => ({ ...x, [o.id]: e.target.value }))} className="h-8 w-28 bg-white" data-testid={`override-amt-${o.id}`} />
        </div>
      ))}
      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" className="h-8 bg-white" data-testid="override-reason" />
      <Button size="sm" className="h-8 bg-terracotta text-xs text-white hover:bg-terracotta-hover" disabled={!reason.trim()} onClick={save} data-testid="override-save">Save override</Button>
    </div>
  );
}

export function PaymentHistory({ payments, open, onDone }) {
  const [edit, setEdit] = useState(null);
  const byId = Object.fromEntries(open.map((o) => [o.id, o]));
  if (!payments.length) return <p className="text-xs text-muted-foreground" data-testid="payments-empty">No supplier payments recorded.</p>;
  return (
    <div className="space-y-3 text-xs" data-testid="payment-history">
      {payments.slice().reverse().map((p) => (
        <div key={p.id} className="border-b border-border pb-2 last:border-0" data-testid={`payment-${p.id}`}>
          <p className="text-charcoal"><span className="font-semibold">{p.payment_date}</span> · {p.bank_reference} · {money(p.amount_usd)} {p.currency} · allocated {money(p.applied_usd)} · unapplied {money(p.unapplied_usd)} · {p.allocation_mode === "OVERRIDE" ? "override" : "oldest first"} · {p.created_by_email}</p>
          {p.allocations.map((a, i) => <p key={i} className="pl-3 text-muted-foreground">→ {byId[a.obligation_id]?.order_number || a.obligation_id.slice(-6)} {byId[a.obligation_id]?.sku || ""} {money(a.amount_cents / 100)}{a.override_reason ? ` (override: ${a.override_reason})` : ""}</p>)}
          <div className="mt-1 flex gap-2">
            {p.has_receipt && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadFile(`/admin/supplier-payments/${p.id}/receipt`, "receipt")} data-testid={`payment-receipt-${p.id}`}>Receipt</Button>}
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEdit(edit === p.id ? null : p.id)} data-testid={`payment-override-${p.id}`}>Override allocation</Button>
          </div>
          {edit === p.id && <OverrideForm payment={p} open={open} onDone={() => { setEdit(null); onDone(); }} />}
        </div>
      ))}
    </div>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function Statements({ sid, statements, canFinalize, onDone }) {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [st, setSt] = useState(null);
  const base = `/admin/suppliers/${sid}/statements/${ym.y}/${ym.m}`;
  const gen = async () => { try { const { data } = await api.get(base); setSt(data); } catch (e) { err(e); } };
  const fin = async () => { try { await api.post(`${base}/finalize`); toast.success("Statement finalized and stored"); gen(); onDone(); } catch (e) { err(e); } };
  return (
    <div className="space-y-3 text-xs" data-testid="statements-block">
      <div className="flex flex-wrap items-center gap-2">
        <select value={ym.m} onChange={(e) => { setYm((x) => ({ ...x, m: Number(e.target.value) })); setSt(null); }} className="h-9 rounded-md border border-border bg-white px-2" data-testid="statement-month">
          {MONTHS.map((l, i) => <option key={l} value={i + 1}>{l}</option>)}
        </select>
        <select value={ym.y} onChange={(e) => { setYm((x) => ({ ...x, y: Number(e.target.value) })); setSt(null); }} className="h-9 rounded-md border border-border bg-white px-2" data-testid="statement-year">
          {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <option key={y}>{y}</option>)}
        </select>
        <Button size="sm" variant="outline" className="h-9 text-xs" onClick={gen} data-testid="statement-generate">Generate statement</Button>
      </div>
      {st && (
        <div className="rounded-md border border-border p-3" data-testid="statement-preview">
          <p className="font-semibold text-charcoal" data-testid="statement-status">{st.statement_number} · {st.status}{st.finalized_at ? ` · finalized ${st.finalized_at.slice(0, 10)} by ${st.finalized_by_email}` : ""}</p>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-5">
            {[["Opening", st.opening_usd, "opening"], ["New obligations", st.new_obligations_usd, "obligations"], ["Payments", st.payments_usd, "payments"], ["Adjustments", st.adjustments_usd, "adjustments"], ["Closing", st.closing_usd, "closing"]].map(([l, v, k]) => (
              <div key={k}><p className="text-warmgrey">{l}</p><p className="num font-semibold" data-testid={`statement-${k}`}>{money(v)}</p></div>
            ))}
          </div>
          <p className="mt-2 text-muted-foreground">{st.sales.length} eligible sale line(s) · {st.payments.length} payment(s) · {st.adjustments.length} adjustment(s){[...st.sales, ...st.payments, ...st.adjustments].some((x) => x.late_correction_of) ? " · includes corrections to an already-finalized month" : ""}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => downloadFile(`${base}/file?format=pdf`, "statement.pdf")} data-testid="statement-pdf">Download PDF</Button>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => downloadFile(`${base}/file?format=xlsx`, "statement.xlsx")} data-testid="statement-xlsx">Download Excel</Button>
            {st.status === "DRAFT" && (canFinalize
              ? <Button size="sm" className="h-8 bg-terracotta text-xs text-white hover:bg-terracotta-hover" onClick={fin} data-testid="statement-finalize">Finalize statement</Button>
              : <span className="text-muted-foreground" data-testid="statement-finalize-founder-only">Only the founder account can finalize.</span>)}
          </div>
        </div>
      )}
      <div data-testid="statement-history">
        {statements.map((s) => (
          <p key={s.period} className="flex flex-wrap items-center gap-2" data-testid={`statement-final-${s.period}`}>
            <span className="font-medium">{s.period}</span> · final · closing {money(s.closing_usd)} · {s.finalized_by_email}
            <button className="text-terracotta underline" onClick={() => downloadFile(`/admin/suppliers/${sid}/statements/${s.period.slice(0, 4)}/${Number(s.period.slice(5))}/file?format=pdf`, s.pdf_name)} data-testid={`statement-final-pdf-${s.period}`}>PDF</button>
            <button className="text-terracotta underline" onClick={() => downloadFile(`/admin/suppliers/${sid}/statements/${s.period.slice(0, 4)}/${Number(s.period.slice(5))}/file?format=xlsx`, s.xlsx_name)} data-testid={`statement-final-xlsx-${s.period}`}>Excel</button>
          </p>
        ))}
      </div>
    </div>
  );
}
